// English is imported statically — it is the runtime fallback for all locales
// until their chunk loads, and the sole source of truth for TS types.
import * as en from './en/index';

export type Locale = 'en' | 'zh' | 'zh-tw' | 'ja' | 'ko' | 'vi';
export type Namespace = keyof typeof en;
export type Keys<NS extends Namespace> = keyof typeof en[NS];
export type LocaleDict = typeof en;

// Runtime dictionary. Starts with English everywhere so useT() always has a
// valid synchronous value. loadLocale() mutates this after a dynamic import
// completes; the i18n store then re-notifies React so components re-render.
export const dict: Record<Locale, LocaleDict> = {
  en,
  zh:    en,
  'zh-tw': en,
  ja:    en,
  ko:    en,
  vi:    en,
};

// Track in-flight loads so concurrent calls for the same locale share one
// Promise rather than issuing duplicate network requests.
const pending: Partial<Record<Locale, Promise<void>>> = {};

export function loadLocale(locale: Locale): Promise<void> {
  if (locale === 'en') return Promise.resolve(); // already loaded statically
  if (pending[locale]) return pending[locale]!;
  const p = (async () => {
    const mod = await (
      locale === 'zh'    ? import('./zh/index')    :
      locale === 'zh-tw' ? import('./zh-tw/index') :
      locale === 'ja'    ? import('./ja/index')    :
      locale === 'ko'    ? import('./ko/index')    :
      /* vi */             import('./vi/index')
    );
    dict[locale] = mod as unknown as LocaleDict;
  })();
  pending[locale] = p;
  return p;
}
